"use client";

import Image, { ImageProps } from "next/image";
import { useState } from "react";
import { ImageIcon } from "lucide-react";
import { cn } from "@/lib/utils";

export interface ImageWithFallbackProps extends Omit<ImageProps, "onError"> {
  /** Shown when the image fails to load. Default: subtle icon + "Image unavailable" */
  fallback?: React.ReactNode;
  /** Optional class for the fallback container */
  fallbackClassName?: string;
  /** Called when image loads successfully (for skeleton/loading UX) */
  onLoad?: () => void;
}

/**
 * Next Image that shows a smooth fallback (icon + text) when the image fails to load or is broken.
 * Use for gallery thumbnails, product images, and any external/proxied image that might 404 or error.
 */
export function ImageWithFallback({
  src,
  alt,
  fallback,
  fallbackClassName,
  className,
  onLoad,
  ...props
}: ImageWithFallbackProps) {
  const [error, setError] = useState(false);

  if (error || !src) {
    const isFill = props.fill === true;
    return (
      <div
        className={cn(
          "flex flex-col items-center justify-center gap-1 bg-muted/50 text-muted-foreground",
          isFill && "absolute inset-0",
          fallbackClassName
        )}
        role="img"
        aria-label={alt || "Image unavailable"}
      >
        {fallback ?? (
          <>
            <ImageIcon className="h-8 w-8 shrink-0 opacity-60" />
            <span className="text-xs">Image unavailable</span>
          </>
        )}
      </div>
    );
  }

  return (
    <Image
      src={src}
      alt={alt}
      className={className}
      onError={() => setError(true)}
      onLoad={onLoad}
      {...props}
    />
  );
}
